import React, { useState, useEffect, useContext } from 'react'

import { TRACKED_STATUSES } from '../../constants/couriers'
import { PACKAGE_DELIVERED, PACKAGE_STATUSES_NAMES } from '../../constants/packageStatus'
import { GREEN_CHECK_ICON, GRAY_CHECK_ICON } from '../../constants/urls'
import { DataContext } from '../../context/DataContext'
import { formatDateShort, parseDate } from '../../utils/dates'
import { capitalize } from '../../utils/string_util'

const FollowPackage: React.FC = () => {
  const { sale, courierEvents } = useContext(DataContext)
  const history: PackageHistory[] = sale?.packageHistory
  const courier: Courier = sale?.courier

  const [historyArray, setHistoryArray] = useState<Record<string, PackageHistory[]>>({})
  const [sortedDays, setSortedDays] = useState<string[]>([])
  const [totalEvents, setTotalEvents] = useState<PackageHistory[]>([])

  useEffect(() => {
    if (!courierEvents || courierEvents.length === 0) {
      setTotalEvents(history)
      return
    }
    const filteredEvents = courierEvents.filter((event: CourierEvent) => !TRACKED_STATUSES.includes(event.courierStatus))
    const parsedCourierEvents = filteredEvents.map((event: CourierEvent) => ({
      date: `${event.createdAt} a. m.`,
      packageStatus: event.statusDescription
    }))
    setTotalEvents([...history, ...parsedCourierEvents])
  }, [courierEvents, history])

  const sortHistory = (packageHistory: PackageHistory[]) => {
    const dict: Record<string, PackageHistory[]> = {}
    packageHistory.forEach((item: PackageHistory) => {
      const day = item.date.slice(0, 10)
      if (!dict[day]) {
        dict[day] = []
      }
      dict[day].push(item)
    })
    return dict
  }

  useEffect(() => {
    if (!totalEvents || totalEvents.length === 0) {
      setHistoryArray({})
      setSortedDays([])
      return
    }

    const dict = sortHistory(totalEvents)

    Object.keys(dict).forEach((day) => {
      dict[day].sort(
        (a, b) => {
          const dateA = parseDate(a.date, 1).getTime()
          const dateB = parseDate(b.date, 1).getTime()
          return dateB - dateA
        }
      )
    })

    const days = Object.keys(dict).sort((a, b) => {
      const dateA = dict[a].length > 0 ? parseDate(dict[a][0].date, 1).getTime() : 0
      const dateB = dict[b].length > 0 ? parseDate(dict[b][0].date, 1).getTime() : 0
      return dateB - dateA
    })

    setHistoryArray(dict)
    setSortedDays(days)
  }, [totalEvents])

  return (
    <div className='gap-4 overflow-x-auto p-4 scrollbar-hide font-[Geist]'>
      <div className='grid grid-cols-2 w-full gap-2 rounded-xl p-4 bg-[#F7F7F7]'>
          <h2 className='text-sm font-medium'>Transporte:</h2>
          <h2 className='text-sm text-gray-500'>{capitalize(courier.name)}</h2>

          <h2 className='text-sm font-medium'>Orden del transporte:</h2>
          <h2 className='text-sm text-gray-500'>{sale.trackingNumber}</h2>
      </div>

      {sortedDays.map((day) => (
        <div key={day} className='flex flex-col w-full gap-2 rounded-xl px-4 py-2 bg-[#F7F7F7] mt-4'>
          <h2 className='text-sm font-semibold text-gray-500'>{formatDateShort(day)}</h2>
          <div className='flex flex-col'>
            {historyArray[day].map((item, index) => {
              const isLast = index === historyArray[day].length - 1
              const hasMultipleItems = historyArray[day].length > 1

              return (
                <div key={item.date} className='flex gap-4 relative pb-4'>
                  <div className='flex flex-col items-center relative'>
                    <div className={`w-10 h-10 mt-1 rounded-full border-2 ${item.packageStatus === PACKAGE_DELIVERED ? 'border-green' : 'border-gray-400'} flex items-center justify-center bg-transparent flex-shrink-0 z-10`}>
                      <img
                        src={`${item.packageStatus === PACKAGE_DELIVERED ? GREEN_CHECK_ICON : GRAY_CHECK_ICON}`}
                        alt={item.packageStatus}
                        className='w-6 h-6'
                      />
                    </div>
                    {!isLast && hasMultipleItems && (
                      <div className='absolute top-11 left-1/2 transform -translate-x-1/2 w-0.5 bg-gray-400' style={{ height: 'calc(70% - 10px)' }} />
                    )}
                  </div>
                  <div className='flex flex-col'>
                    <h3 className='text-base'>{PACKAGE_STATUSES_NAMES[item.packageStatus as keyof typeof PACKAGE_STATUSES_NAMES] ?? item.packageStatus}</h3>
                    <h4 className='text-sm text-gray-500'>{item.date.split(', ')[1].split(' ')[0]}{item.location ? `- ${item.location}` : ''}</h4>
                  </div>
                </div>
              )
            })}
          </div>
        </div>
      ))}
    </div>
  )
}

export default FollowPackage
